import { Components } from "@thisgorilla/primate-ds";
import { motion, useIsPresent } from "framer-motion";
import { useState } from "react";
import styled from "styled-components";
import { API_CALLS } from "../../../../../../../API/api_calls";
import { useAppSelector } from "../../../../../../../redux";
import { SPRING_ANIMATION_CONFIG } from "../../../../../../../utils";
import { FlexRowCenter } from "../../../../../../Common/Styles";
import { usePostCardContext } from "../../PostCardContent";

type ReactionsTrayProps = {
  onEmojiClick: (emojiId: string) => void;
};

export const ReactionsTray = ({ onEmojiClick }: ReactionsTrayProps) => {
  const { postId: post_id } = usePostCardContext();
  const { processed_posts } = useAppSelector((state) => state.newsfeed);
  const post = processed_posts[post_id];

  const { reactions } = useAppSelector((state) => state.reactions);
  const [hoveredEmojiId, setHoveredEmojiId] = useState("0");
  const isPresent = useIsPresent();

  const reactionsEmojis = Object.entries(reactions).map(([id, reaction]) => {
    const isHovered = hoveredEmojiId === id;
    const {
      addAReactionToAPost,
      removeAReactionFromAPost,
      updateAReactionToAPost,
    } = API_CALLS.POST_REACTIONS;

    return (
      <EmojiContainer key={id}>
        <Components.Tooltip
          type="label"
          label={reaction.name}
          variant="minimal"
          mode="dark"
        >
          <Emoji
            onClick={() => {
              onEmojiClick(id);
              const { reacted, reaction_id, reaction_type_id } =
                post.user_reacted;
              const reaction_type = parseInt(id);

              if (!reacted) {
                addAReactionToAPost({ post_id, reaction_type });
                return;
              }

              const isSameReactionAsBefore = reaction_type_id === parseInt(id);
              if (isSameReactionAsBefore) {
                removeAReactionFromAPost({
                  post_id,
                  reaction_id,
                  reaction_type,
                });
                return;
              }

              updateAReactionToAPost({ post_id, reaction_id, reaction_type });
            }}
            onMouseOver={() => setHoveredEmojiId(id)}
            onMouseOut={() => setHoveredEmojiId("0")}
            animate={{
              height: isHovered ? "44px" : "34px",
              width: isHovered ? "44px" : "34px",
              fontSize: isHovered ? "32px" : "28px",
              rotate: isHovered ? -30 : 0,
              padding: isHovered ? "4px" : "0px",
              boxShadow: isHovered ? "0px 0px 4px rgba(0, 0, 0, 0.25)" : "none",
            }}
            transition={SPRING_ANIMATION_CONFIG}
            $background={
              post.user_reacted.reaction_type_id === parseInt(id)
                ? reaction.background_color
                : "transparent"
            }
          >
            {reaction.primary_reaction}
          </Emoji>
        </Components.Tooltip>
      </EmojiContainer>
    );
  });

  return (
    <PositionWrapper>
      <EmojisContainer
        $gapInPx={8}
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        exit={{ y: 40 }}
        transition={{
          ...SPRING_ANIMATION_CONFIG,
          ...(!isPresent && {
            type: "tween",
            duration: 0.1,
            ease: "easeOut",
          }),
        }}
      >
        {reactionsEmojis}
      </EmojisContainer>
    </PositionWrapper>
  );
};

const PositionWrapper = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
`;

const EmojisContainer = styled(motion.create(FlexRowCenter))(
  ({ theme }) => `
  width:fit-content;
  border-radius: ${theme.radius["radius-xs"].value}rem;
  padding-top: ${theme.spacing["spacing-2"].value}rem;
  padding-right: ${theme.spacing["spacing-3"].value}rem;
  padding-bottom: ${theme.spacing["spacing-2"].value}rem;
  padding-left: ${theme.spacing["spacing-3"].value}rem;
  box-shadow: ${theme.boxShadows["box-shadow-m"]};
  background: ${theme.Color.elevation.surface["surface-primary"].value};
`
);

const EmojiContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 44px;
`;
const Emoji = styled(motion.span)<{ $background?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  background: ${({ $background }) => $background};
`;
